import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Tab } from '@features/ui';
import {
  ApprovePayload,
  StatusReq,
  TAwaitingApprovalRes,
  TTab,
} from '@src/types';
import { AwaitingApprovalTable } from '@features/admin/status/AwaitingApprovalTable.tsx';
import { PendingPerformanceAccountTable } from '@features/admin/status/PendingPerformanceAccountTable.tsx';
import {
  approveStatus,
  blockStatus,
  bulkApproveStatus,
  denyStatus,
  getAwaitingApproval,
  getPendingPerformanceAccount,
  resendInvite,
} from '@src/services/status.ts';
import { toast } from 'react-toastify';

export const PendingEval: FC = () => {
  const [tabActive, setTabActive] = useState(0);
  const [awaitingApprovalPage, setAwaitingApprovalPage] = useState(1);
  const [awaitingApprovalData, setAwaitingApprovalData] =
    useState<TAwaitingApprovalRes | null>(null);
  const [pendingPerformanceAccountsPage, setPendingPerformanceAccountsPage] =
    useState(1);
  const [pendingPerformanceAccountsData, setPendingPerformanceAccountsData] =
    useState<TAwaitingApprovalRes | null>(null);
  const [selectedItems, setSelectedItems] = useState<ApprovePayload[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const fetchAwaitingApproval = async (params: StatusReq) => {
    const res = await getAwaitingApproval(params);

    setAwaitingApprovalData(res.data);
  };

  const fetchPendingPerformanceAccounts = async (params: StatusReq) => {
    const res = await getPendingPerformanceAccount(params);

    setPendingPerformanceAccountsData(res.data);
  };

  const handleSelectItem = (item: ApprovePayload) => {
    setSelectedItems((prev) => {
      const isSelected = prev.some(
        (selected) => selected.leeloo_account_id === item.leeloo_account_id
      );

      return isSelected
        ? prev.filter(
            (selected) => selected.leeloo_account_id !== item.leeloo_account_id
          )
        : [...prev, item];
    });
    setSelectAll(false);
  };

  const handleSelectAll = (items: ApprovePayload[]) => {
    setSelectedItems(items);
    setSelectAll(items.length > 0);
  };

  const handleBulkApprove = async () => {
    try {
      const res = await bulkApproveStatus(selectedItems);

      setSelectedItems([]);
      setSelectAll(false);
      await fetchAwaitingApproval({ page: awaitingApprovalPage });
      await fetchPendingPerformanceAccounts({
        page: pendingPerformanceAccountsPage,
      });
      toast.success(res?.data?.message || 'Bulk approved successfully.');
    } catch (error) {
      toast.error('Bulk approve failed. Please try again.');
      console.error('Bulk approve failed:', error);
    }
  };

  const handleApprove = useCallback(
    async (payload: ApprovePayload) => {
      try {
        const res = await approveStatus(payload);

        setSelectedItems((prev) =>
          prev.filter(
            (item) => item.leeloo_account_id !== payload.leeloo_account_id
          )
        );
        await fetchAwaitingApproval({ page: awaitingApprovalPage });
        await fetchPendingPerformanceAccounts({
          page: pendingPerformanceAccountsPage,
        });
        toast.success(
          res?.data?.message || 'Evaluation approved successfully.'
        );
      } catch {
        toast.error('Evaluation approve failed. Please try again.');
      }
    },
    [awaitingApprovalPage, pendingPerformanceAccountsPage]
  );

  const handleBlock = useCallback(
    async (payload: ApprovePayload) => {
      try {
        const res = await blockStatus(payload);

        setSelectedItems((prev) =>
          prev.filter(
            (item) => item.leeloo_account_id !== payload.leeloo_account_id
          )
        );
        await fetchAwaitingApproval({ page: awaitingApprovalPage });
        await fetchPendingPerformanceAccounts({
          page: pendingPerformanceAccountsPage,
        });

        toast.success(res?.data?.message || 'Evaluation blocked successfully.');
      } catch {
        toast.error('Evaluation block failed. Please try again.');
      }
    },
    [awaitingApprovalPage, pendingPerformanceAccountsPage]
  );

  const handleResend = useCallback(async (payload: ApprovePayload) => {
    try {
      const res = await resendInvite(payload);

      toast.success(
        res?.data?.message || 'Approval email resent resent successfully.'
      );
    } catch {
      toast.error('Resend approval email failed. Please try again.');
    }
  }, []);

  const handleDeny = useCallback(
    async (payload: ApprovePayload) => {
      try {
        const res = await denyStatus(payload);

        setSelectedItems((prev) =>
          prev.filter(
            (item) => item.leeloo_account_id !== payload.leeloo_account_id
          )
        );
        await fetchAwaitingApproval({ page: awaitingApprovalPage });
        await fetchPendingPerformanceAccounts({
          page: pendingPerformanceAccountsPage,
        });
        toast.success(res?.data?.message || 'Evaluation denied successfully.');
      } catch {
        toast.error('Evaluation deny failed. Please try again.');
      }
    },
    [awaitingApprovalPage, pendingPerformanceAccountsPage]
  );

  useEffect(() => {
    fetchAwaitingApproval({ page: awaitingApprovalPage }).then();
  }, [awaitingApprovalPage]);

  useEffect(() => {
    fetchPendingPerformanceAccounts({
      page: pendingPerformanceAccountsPage,
    }).then();
  }, [pendingPerformanceAccountsPage]);

  const tabs: TTab[] = useMemo(
    () => [
      {
        label: 'Awaiting Approval',
        component: (
          <AwaitingApprovalTable
            currentPage={awaitingApprovalPage}
            data={awaitingApprovalData?.data ?? []}
            selectAll={selectAll}
            selectedItems={selectedItems}
            totalPages={awaitingApprovalData?.total_pages || 1}
            onApprove={handleApprove}
            onBlock={handleBlock}
            onChangePage={setAwaitingApprovalPage}
            onDeny={handleDeny}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectItem}
          />
        ),
        count: awaitingApprovalData?.total,
      },
      {
        label: 'Awaiting Document Signature / Purchase',
        component: (
          <PendingPerformanceAccountTable
            currentPage={pendingPerformanceAccountsPage}
            data={pendingPerformanceAccountsData?.data ?? []}
            totalPages={pendingPerformanceAccountsData?.total_pages || 1}
            onBlock={handleBlock}
            onChangePage={setPendingPerformanceAccountsPage}
            onResend={handleResend}
          />
        ),
        count: pendingPerformanceAccountsData?.total,
      },
    ],
    [
      awaitingApprovalPage,
      awaitingApprovalData?.data,
      awaitingApprovalData?.total_pages,
      awaitingApprovalData?.total,
      selectAll,
      selectedItems,
      handleApprove,
      pendingPerformanceAccountsPage,
      pendingPerformanceAccountsData?.data,
      pendingPerformanceAccountsData?.total_pages,
      pendingPerformanceAccountsData?.total,
    ]
  );

  return (
    <div className={'relative'}>
      <Tab
        className={'mt-1.5 mb-5.5'}
        size={'sm'}
        tabActive={tabActive}
        tabs={tabs}
        onChangeTab={setTabActive}
      />
      <Button
        className={'text-[#B6D72C] text-sm underline absolute -top-2 right-0'}
        disabled={!selectedItems.length}
        variant={'link'}
        onClick={handleBulkApprove}
      >
        Approve All Checked
      </Button>
    </div>
  );
};
